// BadgeEarnedPopup.js — Celebration modal when a badge is earned

import React, { useEffect } from 'react';
import { theme } from '../theme';
import { BadgeDisplayLarge } from './BadgeDisplay';

function BadgeEarnedPopup({ badge, onDismiss }) {
  // Auto-dismiss after 8 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss();
    }, 8000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  if (!badge) return null;

  const categoryLabel = {
    streak: 'Streak Achievement',
    perfect: 'Perfect Day Achievement',
    days: 'Milestone Achievement',
    scroll: 'Scroll Complete',
    journey: 'Journey Achievement',
  }[badge.category] || 'Achievement Unlocked';

  return (
    <div style={styles.overlay} onClick={onDismiss}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        {/* Animated glow ring behind badge */}
        <div style={styles.badgeWrap}>
          <div
            style={{
              ...styles.glowRing,
              boxShadow: `0 0 40px ${badge.color}60, 0 0 80px ${badge.color}30`,
            }}
          />
          <BadgeDisplayLarge badge={badge} />
        </div>

        <div style={styles.categoryLabel}>{categoryLabel}</div>

        <h2 style={{ ...styles.badgeName, color: badge.color }}>
          {badge.name}
        </h2>

        <p style={styles.badgeDescription}>
          {badge.description}
        </p>

        <p style={styles.mgsTagline}>
          My Greatest Self
        </p>

        <div style={styles.buttons}>
          <button
            style={{ ...styles.shareButton, backgroundColor: badge.color }}
            onClick={() => {
              // Share functionality — Phase 5
              // For now just dismiss
              onDismiss();
            }}
          >
            Share This Achievement
          </button>
          <button style={styles.dismissButton} onClick={onDismiss}>
            Continue
          </button>
        </div>

        {/* Progress bar showing auto-dismiss */}
        <div style={styles.progressBar}>
          <div style={{ ...styles.progressFill, backgroundColor: badge.color }} />
        </div>

      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
  },
  modal: {
    backgroundColor: theme.colors.bgMedium,
    borderRadius: '24px',
    padding: '40px 32px 28px',
    maxWidth: '380px',
    width: '100%',
    border: `1px solid ${theme.colors.bgLight}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  badgeWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '8px',
  },
  glowRing: {
    position: 'absolute',
    width: '140px',
    height: '140px',
    borderRadius: '50%',
    animation: 'pulse 2s ease-in-out infinite',
  },
  categoryLabel: {
    color: theme.colors.textSecondary,
    fontSize: '0.6875rem',
    fontWeight: '700',
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },
  badgeName: {
    fontSize: '1.5rem',
    fontWeight: '700',
    margin: 0,
    lineHeight: '1.2',
  },
  badgeDescription: {
    color: theme.colors.textSecondary,
    fontSize: '0.875rem',
    lineHeight: '1.6',
    margin: 0,
    maxWidth: '280px',
  },
  mgsTagline: {
    color: theme.colors.textMuted,
    fontSize: '0.6875rem',
    fontStyle: 'italic',
    margin: 0,
    letterSpacing: '1px',
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    width: '100%',
    marginTop: '8px',
  },
  shareButton: {
    border: 'none',
    borderRadius: '10px',
    padding: '0.9333em 1.6em',
    color: 'white',
    fontSize: '0.9375rem',
    fontWeight: '700',
    cursor: 'pointer',
    width: '100%',
  },
  dismissButton: {
    backgroundColor: 'transparent',
    border: `1px solid ${theme.colors.bgLight}`,
    borderRadius: '10px',
    padding: '0.8571em 1.7143em',
    color: theme.colors.textSecondary,
    fontSize: '0.875rem',
    cursor: 'pointer',
    width: '100%',
  },
  progressBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '3px',
    backgroundColor: theme.colors.bgLight,
    borderRadius: '0 0 24px 24px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    width: '100%',
    animation: 'shrink 8s linear forwards',
    transformOrigin: 'left',
  },
};

// Inject keyframe animations
const styleTag = document.createElement('style');
styleTag.textContent = `
  @keyframes pulse {
    0%, 100% { opacity: 0.4; transform: scale(1); }
    50% { opacity: 0.7; transform: scale(1.08); }
  }
  @keyframes shrink {
    from { transform: scaleX(1); }
    to { transform: scaleX(0); }
  }
`;
if (!document.head.querySelector('#badge-popup-styles')) {
  styleTag.id = 'badge-popup-styles';
  document.head.appendChild(styleTag);
}

export default BadgeEarnedPopup;